// TSL Activity Service
// Handles logging, storage, and retrieval of TSL execution history

import type { TSLActivityEntry, TSLActivityFilter, TSLActivityStats, TSLActivityExport } from '../types/tslActivity';

const TSL_ACTIVITY_STORAGE_KEY = 'mexc_tsl_activity_log';
const MAX_LOG_ENTRIES = 1000; // Keep last 1000 entries
const LOG_RETENTION_HOURS = 48; // Keep logs for 48 hours

export class TSLActivityService {
  private static instance: TSLActivityService;
  private listeners: ((entries: TSLActivityEntry[]) => void)[] = [];

  static getInstance(): TSLActivityService {
    if (!TSLActivityService.instance) {
      TSLActivityService.instance = new TSLActivityService();
    }
    return TSLActivityService.instance;
  }

  /**
   * Log a TSL activity entry
   */
  logActivity(entry: Omit<TSLActivityEntry, 'id' | 'timestamp'>): void {
    const fullEntry: TSLActivityEntry = {
      ...entry,
      id: this.generateId(),
      timestamp: Date.now(),
    };

    console.log('📝 TSL Activity Logged:', {
      type: fullEntry.eventType,
      symbol: fullEntry.symbol,
      success: fullEntry.success,
      reason: fullEntry.reason
    });

    const entries = this.getEntries();
    entries.unshift(fullEntry); // Add to beginning

    // Trim to max entries and retention period
    const trimmedEntries = this.trimEntries(entries);
    
    this.saveEntries(trimmedEntries);
    this.notifyListeners(trimmedEntries);
  }

  /**
   * Get all TSL activity entries
   */
  getEntries(): TSLActivityEntry[] {
    try {
      const stored = localStorage.getItem(TSL_ACTIVITY_STORAGE_KEY);
      if (!stored) return [];
      
      const entries: TSLActivityEntry[] = JSON.parse(stored);
      return this.trimEntries(entries); // Clean up old entries on read
    } catch (error) {
      console.error('Error loading TSL activity entries:', error);
      return [];
    }
  }

  /**
   * Get filtered TSL activity entries
   */
  getFilteredEntries(filter: TSLActivityFilter): TSLActivityEntry[] {
    const entries = this.getEntries();
    
    return entries.filter(entry => {
      if (filter.symbol && entry.symbol !== filter.symbol) return false;
      if (filter.positionId && entry.positionId !== filter.positionId) return false;
      if (filter.eventType && entry.eventType !== filter.eventType) return false;
      if (filter.success !== undefined && entry.success !== filter.success) return false;
      if (filter.dateFrom && entry.timestamp < filter.dateFrom) return false;
      if (filter.dateTo && entry.timestamp > filter.dateTo) return false;
      
      return true;
    });
  }

  /**
   * Get TSL activity statistics
   */
  getStats(): TSLActivityStats {
    const entries = this.getEntries();
    const updateEntries = entries.filter(e => e.eventType === 'update');
    
    const symbolsSet = new Set(entries.map(e => e.symbol));
    const processingTimes = entries
      .filter(e => e.processingDuration)
      .map(e => e.processingDuration!);

    return {
      totalEntries: entries.length,
      successfulUpdates: updateEntries.filter(e => e.success).length,
      failedUpdates: updateEntries.filter(e => !e.success).length,
      symbolsTracked: Array.from(symbolsSet),
      lastActivity: entries.length > 0 ? entries[0].timestamp : undefined,
      averageProcessingTime: processingTimes.length > 0 
        ? processingTimes.reduce((a, b) => a + b, 0) / processingTimes.length 
        : undefined
    };
  }

  /**
   * Export TSL activity data
   */
  exportData(filter?: TSLActivityFilter): TSLActivityExport {
    const entries = filter ? this.getFilteredEntries(filter) : this.getEntries();
    
    const timestamps = entries.map(e => e.timestamp);
    const minTime = Math.min(...timestamps);
    const maxTime = Math.max(...timestamps);

    return {
      exportDate: new Date().toISOString(),
      totalEntries: entries.length,
      dateRange: {
        from: new Date(minTime).toISOString(),
        to: new Date(maxTime).toISOString()
      },
      entries
    };
  }

  /**
   * Export as CSV string
   */
  exportAsCSV(filter?: TSLActivityFilter): string {
    const entries = filter ? this.getFilteredEntries(filter) : this.getEntries();
    
    const headers = [
      'Timestamp',
      'Symbol',
      'Position ID',
      'Event Type',
      'Success',
      'Previous Stop Price',
      'New Stop Price',
      'Market Price',
      'Reason',
      'Error Message',
      'Trailing Distance',
      'Trailing Type',
      'High Water Mark',
      'Low Water Mark',
      'Profit Amount',
      'Profit %',
      'API Response Code',
      'Processing Duration (ms)'
    ];

    const rows = entries.map(entry => [
      new Date(entry.timestamp).toISOString(),
      entry.symbol,
      entry.positionId.toString(),
      entry.eventType,
      entry.success.toString(),
      entry.previousStopPrice?.toString() || '',
      entry.newStopPrice?.toString() || '',
      entry.currentMarketPrice.toString(),
      entry.reason,
      entry.errorMessage || '',
      entry.trailingDistance?.toString() || '',
      entry.trailingType || '',
      entry.highWaterMark?.toString() || '',
      entry.lowWaterMark?.toString() || '',
      entry.profitAmount?.toString() || '',
      entry.profitPercentage?.toString() || '',
      entry.apiResponseCode?.toString() || '',
      entry.processingDuration?.toString() || ''
    ]);

    return [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');
  }

  /**
   * Clear all activity entries
   */
  clearEntries(): void {
    localStorage.removeItem(TSL_ACTIVITY_STORAGE_KEY);
    this.notifyListeners([]);
  }

  /**
   * Add listener for activity updates
   */
  addListener(listener: (entries: TSLActivityEntry[]) => void): void {
    this.listeners.push(listener);
  }

  /**
   * Remove listener
   */
  removeListener(listener: (entries: TSLActivityEntry[]) => void): void {
    this.listeners = this.listeners.filter(l => l !== listener);
  }

  private generateId(): string {
    return `tsl_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private trimEntries(entries: TSLActivityEntry[]): TSLActivityEntry[] {
    const now = Date.now();
    const retentionCutoff = now - (LOG_RETENTION_HOURS * 60 * 60 * 1000);
    
    // Filter by retention period and limit count
    return entries
      .filter(entry => entry.timestamp > retentionCutoff)
      .slice(0, MAX_LOG_ENTRIES);
  }

  private saveEntries(entries: TSLActivityEntry[]): void {
    try {
      localStorage.setItem(TSL_ACTIVITY_STORAGE_KEY, JSON.stringify(entries));
    } catch (error) {
      console.error('Error saving TSL activity entries:', error);
    }
  }

  private notifyListeners(entries: TSLActivityEntry[]): void {
    this.listeners.forEach(listener => {
      try {
        listener(entries);
      } catch (error) {
        console.error('Error notifying TSL activity listener:', error);
      }
    });
  }
}

// Export singleton instance
export const tslActivityService = TSLActivityService.getInstance();
